import rules from '../../../data/tree_size_rules_v1.json';
import { readItemField } from './item-field-reader';
import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { createFormulaReadContext } from './formula-read-context';

type Profile = { defaultSize: string; sourcesBySize: Record<string, string> };
const profiles: Record<string, Profile> = rules.profiles;
const record = (v: unknown): Record<string, unknown> => v !== null && typeof v === 'object' ? v as Record<string, unknown> : {};

/** Item-side twin of ft_apply_tree_size_rules; the host owns template resolution. */
export function applyTreeSizeRules(template: ItemFieldRow, row: ItemFieldRow,
    resolveTemplate: (id: string) => ItemFieldRow | null): ItemFieldRow {
    const itemId = readItemField(template, 'item_id');
    if (readItemField(template, 'item_type') !== 'tree' || typeof itemId !== 'string') return template;
    const profile = profiles[itemId];
    if (!profile) return template;
    const size = readInventoryField({ inventory: row }, 'inventory_size') ?? profile.defaultSize;
    const id = typeof size === 'string' ? profile.sourcesBySize[size] : undefined;
    if (!id || id === itemId) return template;
    const source = resolveTemplate(id);
    if (!source) throw new Error(`Missing tree size rule source ${id}`);
    const layers = { ...record(record(template).item_layer_types) };
    const sourceLayers = record(record(source).item_layer_types);
    const { layerValue } = createFormulaReadContext({ catalogRows: {}, abilityTypes: [],
        aggregationStrategy: () => 'host_wins', resolveCarrierRef: resolveTemplate, resolveInventoryInstance: () => null });
    if (readItemField(source, 'item_type') !== 'tree' || layerValue(source, 'size') !== size) {
        throw new Error(`Missing or mismatched tree size rule source ${id}`);
    }
    if (layerValue(source, 'item_composition') !== layerValue(template, 'item_composition')) {
        throw new Error('Tree size rules cannot change material');
    }
    for (const key of ['ability_dc', 'requires_tool', 'fabricate', 'roll_type', 'skill_roll_contester',
        'gathering_time_int', 'gathering_time_type', 'immovable', 'creature_environment']) {
        layers[key] = sourceLayers[key] ?? null;
    }
    return { ...template, item_layer_types: layers };
}
